import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { CornerDownLeft, Search } from 'lucide-react';
import { visibleNavItems, type NavItem } from './nav';
import { useRole } from '../lib/useRole';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

function score(item: NavItem, q: string): boolean {
  if (!q) return true;
  const haystack = [item.label, item.hint, item.path, ...(item.keywords ?? [])]
    .join(' ')
    .toLowerCase();
  // every typed word has to appear somewhere — cheap fuzzy that never surprises
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word));
}

export default function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const [, setLocation] = useLocation();
  const role = useRole();
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const results = useMemo(
    () => visibleNavItems(role).filter((item) => score(item, query)),
    [role, query],
  );

  // Reset every time it opens so it always starts from a clean slate.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setCursor(0);
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${cursor}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  if (!open) return null;

  const go = (item: NavItem) => {
    setLocation(item.path);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || (e.key === 'n' && e.ctrlKey)) {
      e.preventDefault();
      setCursor((c) => (results.length ? (c + 1) % results.length : 0));
    } else if (e.key === 'ArrowUp' || (e.key === 'p' && e.ctrlKey)) {
      e.preventDefault();
      setCursor((c) => (results.length ? (c - 1 + results.length) % results.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const item = results[cursor];
      if (item) go(item);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center px-4 pt-[12vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Jump to a section"
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-neutral-950/70 backdrop-blur-sm motion-safe:animate-[fadeIn_120ms_ease-out]"
      />

      <div
        onKeyDown={onKeyDown}
        className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-neutral-700/70 bg-neutral-900/95 shadow-[0_24px_70px_-12px_rgba(0,0,0,0.9)] ring-1 ring-neutral-800/60 backdrop-blur-xl motion-safe:animate-[popIn_140ms_cubic-bezier(0.2,0.9,0.3,1)]"
      >
        <div className="flex items-center gap-3 border-b border-neutral-800 px-4">
          <Search className="h-4 w-4 shrink-0 text-neutral-500" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Jump to…"
            aria-label="Search sections"
            className="w-full bg-transparent py-3.5 text-[15px] text-neutral-100 placeholder:text-neutral-500 focus:outline-none"
          />
          <kbd className="hidden shrink-0 rounded border border-neutral-700 px-1.5 py-0.5 text-[10px] font-medium text-neutral-500 sm:block">
            ESC
          </kbd>
        </div>

        <div ref={listRef} className="max-h-[min(24rem,50vh)] overflow-y-auto p-2">
          {results.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-neutral-500">
              Nothing matches “{query}”.
            </p>
          )}
          {results.map((item, i) => {
            const Icon = item.icon;
            const active = i === cursor;
            return (
              <button
                key={item.path}
                data-index={i}
                onClick={() => go(item)}
                onMouseMove={() => setCursor(i)}
                className={`flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                  active ? 'bg-neutral-800 text-neutral-50' : 'text-neutral-300'
                }`}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border transition-colors ${
                    active
                      ? 'border-primary-500/40 bg-primary-500/15 text-primary-400'
                      : 'border-neutral-800 bg-neutral-900 text-neutral-500'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-medium">{item.label}</span>
                  <span className="block truncate text-xs text-neutral-500">{item.hint}</span>
                </span>
                {active && <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-neutral-500" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
